import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useLang, shared, ROUTES, COSA_FACCIAMO_PATHS } from "../i18n";

const Wordmark = ({ dark = false, onClick }) => (
  <Link to="/" onClick={onClick} data-testid="wordmark-link" className="group flex flex-col justify-center leading-none">
    <span
      className={`font-serif text-[22px] font-medium tracking-[0.32em] transition-colors duration-500 ${
        dark ? "text-ivory" : "text-ink"
      }`}
    >
      NICOLE SRL
    </span>
  </Link>
);

const LangToggle = ({ dark = false }) => {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid="lang-toggle"
      aria-label="Switch language"
      className={`flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
        dark ? "text-ivory/70 hover:text-ivory" : "text-stone hover:text-ink"
      }`}
    >
      <span className={lang === "it" ? (dark ? "text-ivory" : "text-ink") : "opacity-50"}>IT</span>
      <span className="opacity-40">/</span>
      <span className={lang === "en" ? (dark ? "text-ivory" : "text-ink") : "opacity-50"}>EN</span>
    </button>
  );
};

export default function Header() {
  const { lang } = useLang();
  const t = shared[lang];
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const homeRoute = ROUTES.find((r) => r.key === "home");
  const cosaFacciamoRoutes = ROUTES.filter((r) => COSA_FACCIAMO_PATHS.includes(r.path));
  const tailRoutes = ROUTES.filter((r) => ["saldo", "chisiamo", "contatti"].includes(r.key));
  const cosaFacciamoActive = COSA_FACCIAMO_PATHS.includes(location.pathname);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const linkCls = (isActive) =>
    `relative text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
      isActive ? "text-ink" : "text-stone hover:text-ink"
    }`;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "border-b border-ink/10 bg-ivory/85 backdrop-blur-md"
            : "border-b border-transparent bg-gradient-to-b from-ivory/90 via-ivory/50 to-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 md:px-10 md:py-5">
          <Wordmark />
          <nav className="hidden items-center gap-7 xl:flex" aria-label="Main navigation">
            <NavLink to={homeRoute.path} data-testid="nav-home" className={({ isActive }) => linkCls(isActive)}>
              {t.nav.home}
            </NavLink>
            <div className="group relative" data-testid="nav-cosafacciamo">
              <button
                className={`flex items-center gap-1.5 py-2 ${linkCls(cosaFacciamoActive)}`}
                aria-haspopup="true"
              >
                {t.nav.cosafacciamo}
                <ChevronDown size={12} strokeWidth={1.5} className="transition-transform duration-300 group-hover:rotate-180" />
              </button>
              <div className="invisible absolute left-1/2 top-full w-64 -translate-x-1/2 pt-2 opacity-0 transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                <div className="border border-ink/10 bg-ivory py-3 shadow-[0_20px_50px_-20px_rgba(26,26,26,0.25)]">
                  {cosaFacciamoRoutes.map((r) => (
                    <NavLink
                      key={r.key}
                      to={r.path}
                      data-testid={`nav-${r.key}`}
                      className={({ isActive }) =>
                        `block px-6 py-3 text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                          isActive ? "text-bronze" : "text-ink/70 hover:bg-dove/50 hover:text-ink"
                        }`
                      }
                    >
                      {t.nav[r.key]}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
            {tailRoutes.map((r) => (
              <NavLink key={r.key} to={r.path} data-testid={`nav-${r.key}`} className={({ isActive }) => linkCls(isActive)}>
                {t.nav[r.key]}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-7 xl:flex">
            <LangToggle />
            <Link
              to="/contatti"
              data-testid="header-cta"
              className="group inline-flex items-center gap-2 border border-ink/70 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-500 hover:bg-ink hover:text-ivory"
            >
              {t.ctaHeader}
              <ArrowUpRight size={13} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <div className="flex items-center gap-6 xl:hidden">
            <LangToggle />
            <button
              onClick={() => setOpen(true)}
              data-testid="menu-open-button"
              aria-label={t.menuOpen}
              className="flex flex-col items-end gap-[7px] py-2"
            >
              <span className="h-px w-7 bg-ink" />
              <span className="h-px w-5 bg-ink" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-ink text-ivory"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-4 md:px-10 md:py-5">
              <Wordmark dark onClick={() => setOpen(false)} />
              <button
                onClick={() => setOpen(false)}
                data-testid="menu-close-button"
                aria-label={t.menuClose}
                className="py-2 text-[11px] font-medium uppercase tracking-[0.25em] text-ivory/70 transition-colors hover:text-ivory"
              >
                {t.menuClose}
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center px-6 py-8 md:px-10" aria-label="Mobile navigation">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                <NavLink
                  to="/"
                  data-testid="mobile-nav-home"
                  className={({ isActive }) =>
                    `block border-b border-ivory/10 py-4 font-serif text-3xl font-light tracking-tight transition-colors duration-300 ${
                      isActive ? "text-bronze" : "text-ivory hover:text-bronze"
                    }`
                  }
                >
                  {t.nav.home}
                </NavLink>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
                <div className="border-b border-ivory/10 py-4">
                  <div className="font-serif text-3xl font-light tracking-tight text-ivory/60">{t.nav.cosafacciamo}</div>
                  <div className="mt-3 flex flex-col gap-2.5 pl-4">
                    {cosaFacciamoRoutes.map((r) => (
                      <NavLink
                        key={r.key}
                        to={r.path}
                        data-testid={`mobile-nav-${r.key}`}
                        className={({ isActive }) =>
                          `text-[11px] font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                            isActive ? "text-bronze" : "text-ivory/70 hover:text-ivory"
                          }`
                        }
                      >
                        {t.nav[r.key]}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </motion.div>
              {tailRoutes.map((r, i) => (
                <motion.div
                  key={r.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.22 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink
                    to={r.path}
                    data-testid={`mobile-nav-${r.key}`}
                    className={({ isActive }) =>
                      `block border-b border-ivory/10 py-4 font-serif text-3xl font-light tracking-tight transition-colors duration-300 ${
                        isActive ? "text-bronze" : "text-ivory hover:text-bronze"
                      }`
                    }
                  >
                    {t.nav[r.key]}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="px-6 pb-10 md:px-10"
            >
              <Link
                to="/contatti"
                data-testid="mobile-menu-cta"
                className="flex items-center justify-center gap-3 border border-ivory/40 py-4 text-[11px] font-medium uppercase tracking-[0.22em] transition-colors duration-500 hover:bg-ivory hover:text-ink"
              >
                {t.ctaHeader}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
